import React, { useEffect, useState, lazy } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, Loader2, Play, Pause, ChevronRight, Search } from 'lucide-react';
// --- SHARED DATA ---
type UseCase = {
  id: string;
  category: string;
  role: string;
  industry: string;
  tool: {
    slug: string;
    name: string;
    light?: boolean;
  };
  title: string;
  result: string;
};
const USE_CASES: UseCase[] = [{
  id: 'uc-1',
  category: 'Ops',
  role: 'Founder',
  industry: 'E-commerce',
  tool: {
    slug: 'stripe',
    name: 'Stripe'
  },
  title: 'Weekly revenue pulse',
  result: 'Closed last week’s books and posted the $82k recap — up 14%'
}, {
  id: 'uc-2',
  category: 'Support',
  role: 'Support manager',
  industry: 'SaaS',
  tool: {
    slug: 'zendesk',
    name: 'Zendesk',
    light: true
  },
  title: 'Ticket triage',
  result: 'Triaged 42 overnight tickets and queued replies before standup'
}, {
  id: 'uc-3',
  category: 'Sales',
  role: 'Sales lead',
  industry: 'Agency',
  tool: {
    slug: 'salesforce',
    name: 'Salesforce'
  },
  title: 'Meeting follow-ups',
  result: 'Sent follow-ups for today’s 4 calls and updated every deal'
}, {
  id: 'uc-4',
  category: 'Ops',
  role: 'Founder',
  industry: 'E-commerce',
  tool: {
    slug: 'shopify',
    name: 'Shopify'
  },
  title: 'Inventory reorder',
  result: 'Caught 12 products running low and placed the reorder'
}, {
  id: 'uc-5',
  category: 'Marketing',
  role: 'Marketer',
  industry: 'Agency',
  tool: {
    slug: 'hubspot',
    name: 'HubSpot'
  },
  title: 'Campaign performance',
  result: 'Tracked summer-campaign ROI across every channel and flagged the winner'
}, {
  id: 'uc-6',
  category: 'Retention',
  role: 'Success lead',
  industry: 'SaaS',
  tool: {
    slug: 'hubspot',
    name: 'HubSpot'
  },
  title: 'Renewal watch',
  result: 'Flagged 7 accounts up for renewal and nudged each owner'
}];
// --- SHARED COMPONENTS ---
function ToolIcon({
  slug,
  light,
  size = 'md'
}: {
  slug: string;
  light?: boolean;
  size?: 'sm' | 'md' | 'lg';
}) {
  const dims = size === 'sm' ? 'w-4 h-4' : size === 'lg' ? 'w-8 h-8' : 'w-5 h-5';
  const containerDims = size === 'sm' ? 'w-6 h-6 rounded-md' : size === 'lg' ? 'w-14 h-14 rounded-xl' : 'w-8 h-8 rounded-lg';
  return <div className={`flex items-center justify-center bg-[#FAF8F4] border border-neutral-200/60 shrink-0 ${containerDims}`} data-id="element-5770">
      <img src={`https://cdn.simpleicons.org/${slug}${light ? '/2B211A' : ''}`} alt="" className={`${dims} object-contain`} loading="lazy" onError={e => {
      const img = e.currentTarget as HTMLImageElement;
      img.src = `https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/${slug}.svg`;
    }} data-id="element-5771" />
    </div>;
}
// --- CONCEPT 1: STICKER WALL ---
function ConceptStickerWall() {
  const [activeId, setActiveId] = useState<string | null>(null);
  const [filter, setFilter] = useState('All');
  const categories = ['All', ...Array.from(new Set(USE_CASES.map(u => u.category)))];
  const filtered = filter === 'All' ? USE_CASES : USE_CASES.filter(u => u.category === filter);
  return <div className="bg-[#FAF8F4] rounded-3xl p-8 md:p-12 border border-neutral-200/60" data-id="element-5772">
      <div className="mb-8 flex flex-wrap gap-2 justify-center" data-id="element-5773">
        {categories.map(c => <button key={c} onClick={() => {
        setFilter(c);
        setActiveId(null);
      }} className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-colors ${filter === c ? 'bg-[#2B211A] text-white' : 'bg-white text-neutral-500 hover:text-[#2B211A] border border-neutral-200'}`} data-id="element-5774">
            {c}
          </button>)}
      </div>

      <div className="flex flex-wrap justify-center gap-4 max-w-4xl mx-auto" data-id="element-5775">
        <AnimatePresence mode="popLayout" data-id="element-5776">
          {filtered.map(uc => {
          const isActive = activeId === uc.id;
          return <motion.div layout initial={{
            opacity: 0,
            scale: 0.8
          }} animate={{
            opacity: 1,
            scale: 1
          }} exit={{
            opacity: 0,
            scale: 0.8
          }} key={uc.id} onClick={() => setActiveId(isActive ? null : uc.id)} className={`relative cursor-pointer bg-white border border-neutral-200/80 shadow-sm hover:shadow-md transition-shadow rounded-2xl overflow-hidden ${isActive ? 'w-full max-w-md' : 'w-auto'}`} data-id="element-5777">
                <div className="flex items-center gap-3 p-4" data-id="element-5778">
                  <ToolIcon slug={uc.tool.slug} light={uc.tool.light} data-id="element-5779" />
                  <span className="font-semibold text-[#2B211A] whitespace-nowrap" data-id="element-5780">
                    {uc.title}
                  </span>
                </div>

                <AnimatePresence data-id="element-5781">
                  {isActive && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} className="px-4 pb-4" data-id="element-5782">
                      <div className="flex items-start gap-3 pt-3 border-t border-neutral-100" data-id="element-5783">
                        <div className="mt-0.5 flex items-center justify-center w-5 h-5 rounded-full bg-[#EA580C] text-white shrink-0" data-id="element-5784">
                          <Check size={12} strokeWidth={3} data-id="element-5785" />
                        </div>
                        <span className="text-sm font-medium text-[#2B211A] leading-snug" data-id="element-5786">
                          {uc.result}
                        </span>
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </motion.div>;
        })}
        </AnimatePresence>
      </div>
    </div>;
}
// --- CONCEPT 2: FILTERABLE CARD GRID ---
function ConceptCardGrid() {
  const [filter, setFilter] = useState('All');
  const categories = ['All', ...Array.from(new Set(USE_CASES.map(u => u.category)))];
  const filtered = filter === 'All' ? USE_CASES : USE_CASES.filter(u => u.category === filter);
  return <div className="bg-white rounded-3xl p-8 md:p-12 border border-neutral-200/60" data-id="element-5787">
      <div className="flex flex-col md:flex-row justify-between items-center gap-4 mb-8" data-id="element-5788">
        <div className="flex gap-2" data-id="element-5789">
          {categories.map(c => <button key={c} onClick={() => setFilter(c)} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${filter === c ? 'bg-[#EA580C]/10 text-[#EA580C]' : 'text-neutral-500 hover:bg-neutral-100'}`} data-id="element-5790">
              {c}
            </button>)}
        </div>
        <div className="relative" data-id="element-5791">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" data-id="element-5792" />
          <input type="text" placeholder="Search use cases..." className="pl-9 pr-4 py-2 rounded-xl border border-neutral-200 text-sm focus:outline-none focus:border-[#EA580C]/50" data-id="element-5793" />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-5794">
        <AnimatePresence mode="popLayout" data-id="element-5795">
          {filtered.map(uc => <motion.div layout initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95
        }} key={uc.id} className="group flex flex-col bg-[#FAF8F4] border border-neutral-200/80 rounded-2xl p-6 hover:border-[#EA580C]/30 transition-colors" data-id="element-5796">
              <div className="flex items-center gap-3 mb-4" data-id="element-5797">
                <ToolIcon slug={uc.tool.slug} light={uc.tool.light} data-id="element-5798" />
                <h3 className="font-bold text-[#2B211A]" data-id="element-5799">{uc.title}</h3>
              </div>
              <div className="mt-auto" data-id="element-5800">
                <div className="flex items-start gap-3 pt-4 border-t border-neutral-200/60" data-id="element-5801">
                  <div className="mt-0.5 flex items-center justify-center w-5 h-5 rounded-full bg-[#EA580C] text-white shrink-0" data-id="element-5802">
                    <Check size={12} strokeWidth={3} data-id="element-5803" />
                  </div>
                  <span className="text-sm font-medium text-[#2B211A] leading-snug" data-id="element-5804">
                    {uc.result}
                  </span>
                </div>
              </div>
            </motion.div>)}
        </AnimatePresence>
      </div>
    </div>;
}
// --- CONCEPT 3: INDUSTRY SWITCHER ---
function ConceptIndustrySwitcher() {
  const industries = Array.from(new Set(USE_CASES.map(u => u.industry)));
  const [activeIndustry, setActiveIndustry] = useState(industries[0]);
  const filtered = USE_CASES.filter(u => u.industry === activeIndustry);
  return <div className="bg-[#2B211A] rounded-3xl p-8 md:p-16 text-white overflow-hidden" data-id="element-5805">
      <div className="max-w-3xl mx-auto text-center mb-12" data-id="element-5806">
        <h2 className="text-3xl md:text-5xl font-title font-medium mb-6" data-id="element-5807">
          What can Giga do for me?
        </h2>
        <div className="inline-flex flex-wrap items-center justify-center gap-3 text-xl md:text-2xl text-neutral-400" data-id="element-5808">
          <span data-id="element-5809">I work in</span>
          <div className="relative inline-block" data-id="element-5810">
            <select value={activeIndustry} onChange={e => setActiveIndustry(e.target.value)} className="appearance-none bg-white/10 border border-white/20 rounded-xl px-4 py-2 pr-10 text-white font-semibold focus:outline-none focus:ring-2 focus:ring-[#EA580C]" data-id="element-5811">
              {industries.map(i => <option key={i} value={i} className="text-black" data-id="element-5812">
                  {i}
                </option>)}
            </select>
            <ChevronRight size={20} className="absolute right-3 top-1/2 -translate-y-1/2 rotate-90 pointer-events-none" data-id="element-5813" />
          </div>
        </div>
        <p className="mt-5 text-sm text-neutral-500" data-id="element-5814">
          Pre-filled from the industry you pick during onboarding — change it
          anytime.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto" data-id="element-5815">
        <AnimatePresence mode="wait" data-id="element-5816">
          {filtered.map(uc => <motion.div key={uc.id + activeIndustry} initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -20
        }} className="bg-white/5 border border-white/10 rounded-2xl p-6 backdrop-blur-sm" data-id="element-5817">
              <div className="flex items-center gap-3 mb-4" data-id="element-5818">
                <ToolIcon slug={uc.tool.slug} light={uc.tool.light} data-id="element-5819" />
                <h3 className="font-bold text-white" data-id="element-5820">{uc.title}</h3>
              </div>
              <div className="flex items-start gap-3 mt-4 pt-4 border-t border-white/10" data-id="element-5821">
                <div className="mt-0.5 flex items-center justify-center w-5 h-5 rounded-full bg-[#EA580C] text-white shrink-0" data-id="element-5822">
                  <Check size={12} strokeWidth={3} data-id="element-5823" />
                </div>
                <span className="text-sm font-medium text-white leading-snug" data-id="element-5824">
                  {uc.result}
                </span>
              </div>
            </motion.div>)}
        </AnimatePresence>
      </div>
    </div>;
}
// --- CONCEPT 4: ANIMATED FEED ---
function ConceptAnimatedFeed() {
  const [isPlaying, setIsPlaying] = useState(true);
  const [visibleCount, setVisibleCount] = useState(1);
  useEffect(() => {
    if (!isPlaying) return;
    const interval = setInterval(() => {
      setVisibleCount(prev => prev < USE_CASES.length ? prev + 1 : 1);
    }, 3000);
    return () => clearInterval(interval);
  }, [isPlaying]);
  return <div className="bg-[#FAF8F4] rounded-3xl p-8 md:p-12 border border-neutral-200/60 flex flex-col md:flex-row gap-12 items-start" data-id="element-5825">
      <div className="flex-1 sticky top-12" data-id="element-5826">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#EA580C]/10 text-[#EA580C] text-xs font-bold uppercase tracking-widest mb-6" data-id="element-5827">
          <span className="relative flex h-2 w-2" data-id="element-5828">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#EA580C] opacity-75" data-id="element-5829"></span>
            <span className="relative inline-flex rounded-full h-2 w-2 bg-[#EA580C]" data-id="element-5830"></span>
          </span>
          Live Activity
        </div>
        <h2 className="text-3xl md:text-4xl font-title font-medium text-[#2B211A] mb-4" data-id="element-5831">
          Watch Giga work.
        </h2>
        <p className="text-neutral-500 mb-8 max-w-sm" data-id="element-5832">
          A simulated feed of real tasks Giga is completing right now across
          different roles and tools.
        </p>
        <button onClick={() => setIsPlaying(!isPlaying)} className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-white border border-neutral-200 text-sm font-bold text-[#2B211A] hover:bg-neutral-50 transition-colors" data-id="element-5833">
          {isPlaying ? <>
              <Pause size={16} data-id="element-5834" /> Pause Feed
            </> : <>
              <Play size={16} data-id="element-5835" /> Resume Feed
            </>}
        </button>
      </div>

      <div className="flex-1 w-full max-w-md flex flex-col gap-4" data-id="element-5836">
        <AnimatePresence initial={false} data-id="element-5837">
          {USE_CASES.slice(0, visibleCount).reverse().map((uc, index) => {
          const isDone = index > 0;
          return <motion.div key={uc.id} initial={{
            opacity: 0,
            y: -20,
            scale: 0.95
          }} animate={{
            opacity: 1,
            y: 0,
            scale: 1
          }} className="bg-white border border-neutral-200/80 rounded-2xl p-5 shadow-sm" data-id="element-5838">
                  <div className="flex items-center justify-between mb-3" data-id="element-5839">
                    <span className="text-[11px] font-bold text-neutral-400 uppercase tracking-wider" data-id="element-5840">
                      {uc.category}
                    </span>
                    <span className="text-xs text-neutral-400" data-id="element-5841">Just now</span>
                  </div>
                  <div className="flex items-center gap-3 mb-1" data-id="element-5842">
                    <ToolIcon slug={uc.tool.slug} light={uc.tool.light} size="sm" data-id="element-5843" />
                    <h3 className="text-sm font-bold text-[#2B211A]" data-id="element-5844">
                      {uc.title}
                    </h3>
                  </div>

                  <div className="mt-3" data-id="element-5845">
                    {isDone ? <div className="flex items-start gap-3 pt-3 border-t border-neutral-100" data-id="element-5846">
                        <div className="mt-0.5 flex items-center justify-center w-5 h-5 rounded-full bg-[#EA580C] text-white shrink-0" data-id="element-5847">
                          <Check size={12} strokeWidth={3} data-id="element-5848" />
                        </div>
                        <span className="text-sm font-medium text-[#2B211A] leading-snug" data-id="element-5849">
                          {uc.result}
                        </span>
                      </div> : <div className="flex items-center gap-3 pt-3 border-t border-neutral-100 opacity-60" data-id="element-5850">
                        <Loader2 size={16} className="text-[#EA580C] animate-spin shrink-0" data-id="element-5851" />
                        <span className="text-sm text-neutral-500 italic" data-id="element-5852">
                          Working...
                        </span>
                      </div>}
                  </div>
                </motion.div>;
        })}
        </AnimatePresence>
      </div>
    </div>;
}
// --- CONCEPT 5: STACKED CARD SHUFFLE ---
function ConceptStackedShuffle() {
  const [cards, setCards] = useState(USE_CASES);
  const handleNext = () => {
    setCards(prev => {
      const newCards = [...prev];
      const first = newCards.shift();
      if (first) newCards.push(first);
      return newCards;
    });
  };
  return <div className="bg-[#1A1410] rounded-3xl p-8 md:p-20 overflow-hidden flex flex-col items-center justify-center min-h-[600px]" data-id="element-5853">
      <div className="text-center mb-12" data-id="element-5854">
        <h2 className="text-3xl font-title font-medium text-white mb-4" data-id="element-5855">
          Swipe through use cases
        </h2>
        <p className="text-neutral-400" data-id="element-5856">
          Click the top card to see the next example.
        </p>
      </div>

      <div className="relative w-full max-w-md h-[400px]" data-id="element-5857">
        <AnimatePresence mode="popLayout" data-id="element-5858">
          {cards.map((uc, index) => {
          const isTop = index === 0;
          const offset = index * 12;
          const scale = 1 - index * 0.05;
          const zIndex = cards.length - index;
          if (index > 2) return null; // Only show top 3
          return <motion.div key={uc.id} layout initial={{
            opacity: 0,
            y: 50
          }} animate={{
            opacity: 1 - index * 0.2,
            y: offset,
            scale,
            zIndex
          }} exit={{
            opacity: 0,
            x: -200,
            rotate: -10
          }} transition={{
            type: 'spring',
            stiffness: 300,
            damping: 25
          }} onClick={isTop ? handleNext : undefined} className={`absolute top-0 left-0 w-full bg-white rounded-3xl p-8 shadow-2xl border border-neutral-200 ${isTop ? 'cursor-pointer hover:-translate-y-1 transition-transform' : ''}`} style={{
            transformOrigin: 'top center'
          }} data-id="element-5859">
                <div className="flex items-center gap-4 mb-6" data-id="element-5860">
                  <ToolIcon slug={uc.tool.slug} light={uc.tool.light} size="lg" data-id="element-5861" />
                  <div data-id="element-5862">
                    <span className="text-xs font-bold text-[#EA580C] uppercase tracking-wider" data-id="element-5863">
                      {uc.category}
                    </span>
                    <h3 className="text-xl font-bold text-[#2B211A]" data-id="element-5864">
                      {uc.title}
                    </h3>
                  </div>
                </div>

                <div className="flex items-start gap-3 pt-4 border-t border-neutral-100" data-id="element-5865">
                  <div className="mt-0.5 flex items-center justify-center w-5 h-5 rounded-full bg-[#EA580C] text-white shrink-0" data-id="element-5866">
                    <Check size={12} strokeWidth={3} data-id="element-5867" />
                  </div>
                  <span className="text-sm font-medium text-[#2B211A] leading-snug" data-id="element-5868">
                    {uc.result}
                  </span>
                </div>
              </motion.div>;
        })}
        </AnimatePresence>
      </div>
    </div>;
}
// --- MAIN PAGE ---
export function UseCasesConcepts() {
  return <div className="min-h-screen bg-white pt-32 pb-24" data-id="element-5869">
      <div className="max-w-[1280px] mx-auto px-6 md:px-10" data-id="element-5870">
        <div className="mb-16" data-id="element-5871">
          <h1 className="text-4xl md:text-5xl font-title font-medium text-[#2B211A] mb-4" data-id="element-5872">
            Use Case Concepts
          </h1>
          <p className="text-lg text-neutral-500 max-w-2xl" data-id="element-5873">
            Five interactive ways to display example use cases on a dedicated
            page. Review the interactions below and let me know which direction
            feels right.
          </p>
        </div>

        <div className="space-y-32" data-id="element-5874">
          <section data-id="element-5875">
            <div className="mb-6" data-id="element-5876">
              <h2 className="text-2xl font-bold text-[#2B211A]" data-id="element-5877">
                1. Sticker Wall (Pinboard)
              </h2>
              <p className="text-neutral-500" data-id="element-5878">
                Dense, free-floating cards. Click to expand the result.
              </p>
            </div>
            <ConceptStickerWall data-id="element-5879" />
          </section>

          <section data-id="element-5880">
            <div className="mb-6" data-id="element-5881">
              <h2 className="text-2xl font-bold text-[#2B211A]" data-id="element-5882">
                2. Filterable Card Grid
              </h2>
              <p className="text-neutral-500" data-id="element-5883">
                Clean, structured index. The most scalable option for many use
                cases.
              </p>
            </div>
            <ConceptCardGrid data-id="element-5884" />
          </section>

          <section data-id="element-5885">
            <div className="mb-6" data-id="element-5886">
              <h2 className="text-2xl font-bold text-[#2B211A]" data-id="element-5887">
                3. Industry Switcher
              </h2>
              <p className="text-neutral-500" data-id="element-5888">
                Personalized entry point keyed to onboarding. "What can Giga do
                for me?"
              </p>
            </div>
            <ConceptIndustrySwitcher data-id="element-5889" />
          </section>

          <section data-id="element-5890">
            <div className="mb-6" data-id="element-5891">
              <h2 className="text-2xl font-bold text-[#2B211A]" data-id="element-5892">
                4. Animated Feed
              </h2>
              <p className="text-neutral-500" data-id="element-5893">
                Simulated live activity stream showing Giga working in
                real-time.
              </p>
            </div>
            <ConceptAnimatedFeed data-id="element-5894" />
          </section>

          <section data-id="element-5895">
            <div className="mb-6" data-id="element-5896">
              <h2 className="text-2xl font-bold text-[#2B211A]" data-id="element-5897">
                5. Stacked Card Shuffle
              </h2>
              <p className="text-neutral-500" data-id="element-5898">
                Playful, focused, Tinder-style stack. Great for mobile.
              </p>
            </div>
            <ConceptStackedShuffle data-id="element-5899" />
          </section>
        </div>
      </div>
    </div>;
}